import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function NosActionsPage() {
  const [openAction, setOpenAction] = useState<string | null>('fle');

  const toggleAction = (actionId: string) => {
    setOpenAction(openAction === actionId ? null : actionId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-24 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block">
              <span className="text-sm font-semibold tracking-wider text-orange-500 uppercase">Notre expertise</span>
            </div>
            
            <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight font-serif">
              Nos actions &amp; formations
            </h1>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              PULSAR FC déploie une gamme complète de formations et d'actions d'accompagnement pour favoriser l'insertion socioprofessionnelle, la citoyenneté positive et le développement des compétences.
            </p>
          </div>
        </div>
      </section>

      {/* Actions en Accordéon */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-6xl">
          <div className="space-y-6">
            
            {/* Action 1 - Formation en Langue Française */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('fle')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-translate-2 text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Formation en Langue Française (FLE)</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'fle' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'fle' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        Nos formations en Français Langue Étrangère (FLE) accompagnent les personnes non-francophones dans leur apprentissage de la langue française, clé essentielle de l'intégration sociale et professionnelle.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Objectifs pédagogiques</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Maîtriser les bases de la communication orale et écrite en français</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Développer l'autonomie dans les démarches administratives</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Faciliter l'insertion professionnelle par la maîtrise linguistique</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Préparer aux certifications officielles (DELF, DALF, TCF)</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Publics concernés</h4>
                        <p className="text-slate-700">Primo-arrivants, demandeurs d'asile, réfugiés, migrants en parcours d'intégration</p>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://public.readdy.ai/ai/img_res/704a88742e57cd9e487cfddee005e821.jpg"
                        alt="Formation FLE"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action 2 - Citoyenneté et Valeurs de la République */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('citoyennete')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-flag-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Citoyenneté Positive, Laïcité &amp; Valeurs de la République</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'citoyennete' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'citoyennete' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        Ces formations visent à transmettre les principes fondamentaux de la République française, à promouvoir le vivre-ensemble et à développer une citoyenneté active et responsable.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Thématiques abordées</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Les valeurs de la République : liberté, égalité, fraternité</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Le principe de laïcité et la neutralité de l'État</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Les droits et devoirs du citoyen</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">La lutte contre les discriminations et le racisme</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">L'engagement citoyen et la participation démocratique</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Modalités</h4>
                        <p className="text-slate-700">Ateliers participatifs, débats citoyens, mises en situation, visites institutionnelles</p>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://readdy.ai/api/search-image?query=diverse%20citizens%20participating%20in%20civic%20education%20workshop%20discussing%20democracy%20values%20and%20republic%20principles%20in%20bright%20modern%20room%20showing%20engagement%20dialogue%20and%20citizenship&width=700&height=800&seq=citizenship-workshop-values&orientation=portrait"
                        alt="Formation citoyenneté"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action 3 - Sensibilisation Numérique */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('numerique')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Sensibilisation aux Dangers du Numérique</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'numerique' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'numerique' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        À l'ère du numérique, il est essentiel de développer un esprit critique face à l'information et d'adopter des usages responsables des outils digitaux.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Axes de sensibilisation</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700"><strong>Fake news et désinformation :</strong> Apprendre à vérifier les sources, identifier les manipulations</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700"><strong>Réseaux sociaux :</strong> Comprendre les algorithmes, protéger sa vie privée, gérer son identité numérique</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700"><strong>Surconsommation des écrans :</strong> Prévenir l'addiction, équilibrer vie numérique et vie réelle</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700"><strong>Cyberharcèlement :</strong> Reconnaître et agir face aux violences en ligne</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Publics</h4>
                        <p className="text-slate-700">Jeunes, parents, éducateurs, tout public souhaitant développer sa culture numérique</p>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://readdy.ai/api/search-image?query=people%20learning%20about%20digital%20safety%20and%20media%20literacy%20in%20workshop%20with%20screens%20showing%20fake%20news%20examples%20and%20social%20media%20awareness%20training%20in%20bright%20modern%20setting&width=700&height=800&seq=digital-literacy-training&orientation=portrait"
                        alt="Sensibilisation numérique"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action 4 - Accompagnement des Publics Vulnérables */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('accompagnement')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-heart-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Accompagnement des Publics Vulnérables</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'accompagnement' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'accompagnement' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        PULSAR FC propose un accompagnement global et personnalisé pour les personnes en situation de fragilité : demandeurs d'emploi, bénéficiaires du RSA, publics discriminés ou nécessitant un soutien psychologique.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Dispositifs d'accompagnement</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Diagnostic de situation et construction de projet professionnel</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Soutien psychologique et écoute bienveillante</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Aide aux démarches administratives et sociales</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Ateliers de remobilisation et de confiance en soi</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Orientation vers les structures partenaires adaptées</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Approche</h4>
                        <p className="text-slate-700">Accompagnement individualisé, écoute active, respect du rythme de chacun, approche globale de la personne</p>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://readdy.ai/api/search-image?query=compassionate%20social%20worker%20counseling%20vulnerable%20person%20in%20warm%20welcoming%20office%20showing%20empathy%20support%20and%20personalized%20guidance%20for%20social%20reintegration&width=700&height=800&seq=vulnerable-support-counseling&orientation=portrait"
                        alt="Accompagnement publics vulnérables"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action 5 - Insertion Professionnelle */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('insertion')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-briefcase-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Insertion Professionnelle &amp; Développement des Compétences</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'insertion' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'insertion' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        Nos programmes d'insertion professionnelle visent à faciliter l'accès ou le retour à l'emploi en développant les compétences techniques, transversales et comportementales nécessaires au monde du travail.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Nos actions</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Ateliers de techniques de recherche d'emploi (CV, lettre de motivation, entretien)</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Formations aux compétences numériques de base</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Développement des soft skills (communication, travail en équipe, gestion du stress)</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Immersions en entreprise et mises en situation professionnelle</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Accompagnement à la création d'activité et d'entreprise</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20skills%20training%20workshop%20with%20people%20learning%20job%20interview%20techniques%20and%20career%20development%20in%20bright%20modern%20training%20room%20showing%20confidence%20and%20professional%20growth&width=700&height=800&seq=professional-insertion-training&orientation=portrait"
                        alt="Insertion professionnelle"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action 6 - Conseil aux Institutions */}
            <div className="border-2 border-slate-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-xl">
              <button
                onClick={() => toggleAction('conseil')}
                className="w-full px-8 py-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-6">
                  <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-line text-3xl text-white"></i>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 text-left">Conseil &amp; Formation pour Institutions</h3>
                </div>
                <i className={`ri-arrow-down-s-line text-3xl text-slate-600 transition-transform duration-300 ${openAction === 'conseil' ? 'rotate-180' : ''}`}></i>
              </button>
              
              {openAction === 'conseil' && (
                <div className="px-8 py-8 bg-slate-50 border-t-2 border-slate-200">
                  <div className="grid lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <p className="text-lg text-slate-700 leading-relaxed">
                        PULSAR FC accompagne les institutions publiques, parapubliques et les entreprises privées dans le développement de leurs politiques d'inclusion, de diversité et de responsabilité sociale.
                      </p>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Services proposés</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Diagnostic et audit des pratiques d'inclusion</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Formation des équipes à la diversité et à la non-discrimination</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Accompagnement dans la mise en place de dispositifs d'insertion</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Conseil en ingénierie de formation et pédagogie</span>
                          </li>
                          <li className="flex items-start gap-3">
                            <i className="ri-checkbox-circle-fill text-slate-900 text-xl flex-shrink-0 mt-1"></i>
                            <span className="text-slate-700">Évaluation d'impact social des programmes</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-slate-900">Secteurs</h4>
                        <p className="text-slate-700">Administrations publiques, collectivités territoriales, établissements parapublics, entreprises privées engagées dans la RSE</p>
                      </div>
                    </div>
                    
                    <div className="rounded-2xl overflow-hidden shadow-lg h-96">
                      <img 
                        src="https://readdy.ai/api/search-image?query=professional%20consulting%20meeting%20with%20institutional%20partners%20in%20modern%20conference%20room%20showing%20strategic%20planning%20collaboration%20and%20corporate%20social%20responsibility%20discussion&width=700&height=800&seq=institutional-consulting-meeting&orientation=portrait"
                        alt="Conseil aux institutions"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>

      {/* Nos Formations en Cartes */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 font-serif mb-4">Nos formations phares</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Des programmes adaptés aux besoins de chaque public
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Formation 1 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=french%20language%20classroom%20with%20diverse%20adult%20students%20learning%20alphabet%20and%20basic%20vocabulary%20in%20bright%20welcoming%20educational%20space%20showing%20integration%20and%20language%20learning&width=600&height=700&seq=fle-card-formation&orientation=portrait"
                alt="FLE Débutant"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-orange-500 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-translate-2"></i>
                  FLE
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">FLE Niveau Débutant</h3>
                <p className="text-white/90">Apprentissage des bases de la langue française pour primo-arrivants</p>
              </div>
            </div>
            
            {/* Formation 2 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=citizenship%20education%20workshop%20with%20diverse%20people%20discussing%20republic%20values%20democracy%20and%20civic%20engagement%20in%20modern%20bright%20room%20showing%20active%20participation&width=600&height=700&seq=citizenship-card-formation&orientation=portrait"
                alt="Citoyenneté"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-slate-900 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-flag-line"></i>
                  Citoyenneté
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Parcours Citoyen</h3>
                <p className="text-white/90">Formation aux valeurs républicaines et à la citoyenneté active</p>
              </div>
            </div>
            
            {/* Formation 3 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=digital%20literacy%20workshop%20with%20adults%20learning%20about%20internet%20safety%20fake%20news%20and%20social%20media%20awareness%20in%20modern%20computer%20lab%20showing%20media%20education&width=600&height=700&seq=digital-card-formation&orientation=portrait"
                alt="Éducation numérique"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-orange-500 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-shield-check-line"></i>
                  Numérique
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Éducation Numérique</h3>
                <p className="text-white/90">Sensibilisation aux fake news et usages responsables du digital</p>
              </div>
            </div>
            
            {/* Formation 4 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=job%20seekers%20in%20professional%20skills%20workshop%20learning%20interview%20techniques%20and%20career%20development%20in%20bright%20training%20room%20showing%20confidence%20building&width=600&height=700&seq=employment-card-formation&orientation=portrait"
                alt="Retour à l'emploi"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-slate-900 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-briefcase-line"></i>
                  Emploi
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Retour à l'Emploi</h3>
                <p className="text-white/90">Accompagnement personnalisé vers l'insertion professionnelle</p>
              </div>
            </div>
            
            {/* Formation 5 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=soft%20skills%20training%20workshop%20with%20people%20learning%20communication%20teamwork%20and%20professional%20behavior%20in%20modern%20bright%20space%20showing%20personal%20development&width=600&height=700&seq=softskills-card-formation&orientation=portrait"
                alt="Compétences transversales"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-orange-500 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-lightbulb-line"></i>
                  Soft Skills
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Compétences Transversales</h3>
                <p className="text-white/90">Développement des soft skills essentiels au monde professionnel</p>
              </div>
            </div>
            
            {/* Formation 6 */}
            <div className="relative rounded-3xl overflow-hidden group hover:scale-105 transition-transform duration-300 h-96">
              <img 
                src="https://readdy.ai/api/search-image?query=entrepreneurship%20training%20workshop%20with%20aspiring%20business%20owners%20learning%20about%20company%20creation%20and%20business%20development%20in%20modern%20bright%20space&width=600&height=700&seq=entrepreneurship-card-formation&orientation=portrait"
                alt="Création d'entreprise"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
              <div className="absolute top-6 left-6">
                <span className="px-4 py-2 bg-slate-900 rounded-full text-white text-sm font-semibold flex items-center gap-2 w-fit">
                  <i className="ri-rocket-line"></i>
                  Entrepreneuriat
                </span>
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <h3 className="text-2xl font-bold mb-2">Création d'Entreprise</h3>
                <p className="text-white/90">Accompagnement à la création d'activité et au développement entrepreneurial</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="text-4xl lg:text-5xl font-bold">Intéressé par nos formations ?</h2>
            <p className="text-xl text-slate-300 leading-relaxed">
              Contactez-nous pour obtenir plus d'informations sur nos programmes, les modalités d'inscription et les financements possibles.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/contact" 
                className="px-12 py-4 bg-orange-500 text-white rounded-full font-semibold hover:bg-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Nous contacter
              </Link>
              <Link 
                to="/publics-accompagnes" 
                className="px-12 py-4 bg-white text-slate-900 rounded-full font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Voir les publics concernés
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
